// NOTE: The backend API currently doesn't support updating defaultPreset field
// This component is prepared for when the backend adds this functionality
// For now, it will only display the current preset value

import { useState } from 'react'
import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import { Edit2Icon } from 'lucide-react'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import { useQueryClient } from '@tanstack/react-query'
import { GameQueryKeys } from '@/modules/game/keys'
import { GameProviderWithBannerListTableData } from '@/modules/game/type'
import useMutationUpdateProvider from '@/modules/game/api/hooks/useMutationUpdateProvider'
import { Label } from '@/common/ui/label'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import useQueryGetPresetId from '@/modules/promotion/api/hooks/useQueryGetPresetId'
import { RtpPresetPrecedenceTooltip } from '@/common/components/RtpPresetPrecedenceTooltip'
import { PresetPriorityHint } from '@/common/components/PresetPriorityHint'

interface EditDefaultPresetIdProps {
	row: GameProviderWithBannerListTableData
}

export default function EditDefaultPresetId({ row }: EditDefaultPresetIdProps) {
	const [isOpen, setIsOpen] = useState(false)
	const [presetValue, setPresetValue] = useState(row.defaultPreset)
	const { mutateAsync: updateProvider, isPending } = useMutationUpdateProvider()
	const queryClient = useQueryClient()
	const { data: presets } = useQueryGetPresetId()

	// Find the current preset to display its note
	const currentPreset = presets?.presets.find(
		(preset) => preset.id === row.defaultPreset,
	)
	const displayValue = currentPreset?.note || row.defaultPreset || '-'

	const handleUpdatePreset = async () => {
		// Prepared code for when backend supports this:
		try {
			await updateProvider({
				id: row.providerId,
				payload: {
					defaultPreset: presetValue || undefined,
				} as any,
			})
			queryClient.invalidateQueries({
				queryKey: [GameQueryKeys.GetGameProviderList],
			})
			toast.success('อัพเดท RTP ตั้งต้น สำเร็จ')
			setIsOpen(false)
		} catch (error) {
			showErrorToast(error as Error)
		}
	}

	const handleClose = () => {
		setIsOpen(false)
		setPresetValue(row.defaultPreset)
	}

	return (
		<>
			<div className="flex items-center gap-2">
				<span className="text-sm">{displayValue}</span>
				<Button
					variant="ghost"
					size="icon"
					className="h-8 w-8"
					onClick={() => setIsOpen(true)}
				>
					<Edit2Icon className="h-4 w-4" />
				</Button>
			</div>

			<Dialog open={isOpen} onOpenChange={handleClose}>
				<DialogContent className="sm:max-w-[425px]">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							แก้ไข RTP ตั้งต้น - {row.displayName}
							<RtpPresetPrecedenceTooltip highlightLevel={6} />
						</DialogTitle>
						<PresetPriorityHint
							where="provider"
							trigger="icon"
							className="shrink-0"
						/>
					</DialogHeader>
					<div className="grid gap-4 py-4">
						<div className="grid gap-2">
							<Label>เลือก RTP Preset</Label>
							<Select
								value={presetValue}
								onValueChange={setPresetValue}
								disabled={isPending}
							>
								<SelectTrigger>
									<SelectValue placeholder="เลือก RTP Preset" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="">ไม่มี</SelectItem>
									{presets?.presets.map((preset) => (
										<SelectItem key={preset.id} value={preset.id}>
											{preset.note}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</div>
					<DialogFooter>
						<Button type="button" variant="outline" onClick={handleClose}>
							ยกเลิก
						</Button>
						<Button onClick={handleUpdatePreset} disabled={isPending}>
							{isPending ? 'กำลังบันทึก...' : 'บันทึก'}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	)
}
